"use client";

import {
    auroraUploadProfile,
    convertImageFileToWebP as convertImageFileToWebPInternal,
    type ImageUploadProfile,
} from "@/lib/image-processing";
import { db } from "@/lib/dexie-db";
import { ConvexError } from "convex/values";

/** Legacy localStorage key — migrated into Dexie on first read, then removed. */
const LEGACY_STORAGE_KEY = "aurora-offline-report-queue-v1";
const CHANGE_EVENT = "aurora-offline-report-queue-changed";

export { auroraUploadProfile, type ImageUploadProfile };
export { convertImageFileToWebPInternal as convertImageFileToWebP };

export class OfflineReportQueueError extends Error {
    constructor(message = "Could not save the report offline. Your browser storage may be full.") {
        super(message);
        this.name = "OfflineReportQueueError";
    }
}

export interface OfflineReportImageMetadata {
    make?: string;
    model?: string;
    lensModel?: string;
    focalLength?: number;
    aperture?: number;
    shutterSpeed?: string;
    iso?: number;
    exposureTime?: number;
}

export interface OfflineStoredImage {
    name: string;
    type: string;
    lastModified: number;
    dataUrl: string;
}

export interface OfflineAuroraReportPayload {
    dateTime: number;
    dIndex?: number;
    geographicLocationLatitude: number;
    geographicLocationLongitude: number;
    locationDescription?: string;
    tags?: string[];
    imageMetadata?: OfflineReportImageMetadata;
}

export interface OfflineCloudReportPayload {
    dateTime: number;
    geographicLocationLatitude: number;
    geographicLocationLongitude: number;
    locationDescription?: string;
}

export type OfflineQueuedReport =
    | {
          id: string;
          ownerUserId: string;
          type: "aurora";
          createdAt: number;
          attempts: number;
          lastAttemptAt?: number;
          /** Epoch ms. Sync waits until this time after a rate-limit response. */
          nextAttemptAt?: number;
          lastError?: string;
          payload: OfflineAuroraReportPayload;
          image?: OfflineStoredImage;
      }
    | {
          id: string;
          ownerUserId: string;
          type: "cloud";
          createdAt: number;
          attempts: number;
          lastAttemptAt?: number;
          /** Epoch ms. Sync waits until this time after a rate-limit response. */
          nextAttemptAt?: number;
          lastError?: string;
          payload: OfflineCloudReportPayload;
      };

type NewQueuedReport = OfflineQueuedReport extends infer Report
    ? Report extends OfflineQueuedReport
        ? Omit<Report, "id" | "createdAt" | "attempts">
        : never
    : never;

function emitQueueChanged(): void {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new Event(CHANGE_EVENT));
}

function isObject(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isOptionalString(value: unknown): value is string | undefined {
    return value === undefined || typeof value === "string";
}

function isOptionalNumber(value: unknown): value is number | undefined {
    return value === undefined || typeof value === "number";
}

function isNumber(value: unknown): value is number {
    return typeof value === "number" && Number.isFinite(value);
}

function isStringArray(value: unknown): value is string[] {
    return Array.isArray(value) && value.every((item) => typeof item === "string");
}

function isImageMetadata(value: unknown): value is OfflineReportImageMetadata {
    if (value === undefined) return true;
    if (!isObject(value)) return false;
    return (
        isOptionalString(value.make) &&
        isOptionalString(value.model) &&
        isOptionalString(value.lensModel) &&
        isOptionalNumber(value.focalLength) &&
        isOptionalNumber(value.aperture) &&
        isOptionalString(value.shutterSpeed) &&
        isOptionalNumber(value.iso) &&
        isOptionalNumber(value.exposureTime)
    );
}

function isStoredImage(value: unknown): value is OfflineStoredImage {
    return (
        isObject(value) &&
        typeof value.name === "string" &&
        typeof value.type === "string" &&
        isNumber(value.lastModified) &&
        typeof value.dataUrl === "string" &&
        parseImageDataUrl(value.dataUrl) !== null
    );
}

function isAuroraPayload(value: unknown): value is OfflineAuroraReportPayload {
    return (
        isObject(value) &&
        isNumber(value.dateTime) &&
        isOptionalNumber(value.dIndex) &&
        isNumber(value.geographicLocationLatitude) &&
        isNumber(value.geographicLocationLongitude) &&
        isOptionalString(value.locationDescription) &&
        (value.tags === undefined || isStringArray(value.tags)) &&
        isImageMetadata(value.imageMetadata)
    );
}

function isCloudPayload(value: unknown): value is OfflineCloudReportPayload {
    return (
        isObject(value) &&
        isNumber(value.dateTime) &&
        isNumber(value.geographicLocationLatitude) &&
        isNumber(value.geographicLocationLongitude) &&
        isOptionalString(value.locationDescription)
    );
}

function isQueuedReport(value: unknown): value is OfflineQueuedReport {
    if (!isObject(value)) return false;
    const baseIsValid =
        typeof value.id === "string" &&
        typeof value.ownerUserId === "string" &&
        isNumber(value.createdAt) &&
        isNumber(value.attempts) &&
        isOptionalNumber(value.lastAttemptAt) &&
        isOptionalNumber(value.nextAttemptAt) &&
        isOptionalString(value.lastError);

    if (!baseIsValid) return false;
    if (value.type === "aurora") {
        return isAuroraPayload(value.payload) && (value.image === undefined || isStoredImage(value.image));
    }
    if (value.type === "cloud") {
        return isCloudPayload(value.payload);
    }
    return false;
}

let legacyMigrationPromise: Promise<void> | null = null;

/**
 * One-time migration of the old localStorage queue into Dexie. IndexedDB has
 * a far bigger quota, which matters because queued aurora reports embed
 * full-resolution WebP images as data URLs.
 */
function migrateLegacyQueue(): Promise<void> {
    if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
        return Promise.resolve();
    }
    legacyMigrationPromise ??= (async () => {
        const raw = window.localStorage.getItem(LEGACY_STORAGE_KEY);
        if (!raw) return;
        try {
            const parsed: unknown = JSON.parse(raw);
            if (Array.isArray(parsed)) {
                const reports = parsed.filter(isQueuedReport);
                if (reports.length > 0) {
                    await db.offlineReportQueue.bulkPut(reports);
                }
            }
        } catch {
            // Corrupt legacy data — drop it.
        }
        window.localStorage.removeItem(LEGACY_STORAGE_KEY);
    })()
        .catch((error) => {
            console.warn("Offline report queue migration failed:", error);
        })
        .finally(() => {
            // Only dedupe concurrent runs; once the key is removed the
            // re-check is a no-op getItem.
            legacyMigrationPromise = null;
        });
    return legacyMigrationPromise;
}

async function readQueue(): Promise<OfflineQueuedReport[]> {
    if (typeof window === "undefined") return [];
    await migrateLegacyQueue();
    const rows = await db.offlineReportQueue.toArray();
    return rows.filter(isQueuedReport);
}

function createQueueId(): string {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
        return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export async function getQueuedReports(ownerUserId?: string): Promise<OfflineQueuedReport[]> {
    if (!ownerUserId) return [];
    const queue = (await readQueue()).filter((report) => report.ownerUserId === ownerUserId);
    return queue.sort((a, b) => a.createdAt - b.createdAt);
}

export function subscribeToOfflineReportQueue(listener: () => void): () => void {
    if (typeof window === "undefined") return () => undefined;

    window.addEventListener(CHANGE_EVENT, listener);
    return () => {
        window.removeEventListener(CHANGE_EVENT, listener);
    };
}

export async function saveQueuedReport(report: NewQueuedReport): Promise<OfflineQueuedReport> {
    const queuedReport: OfflineQueuedReport = {
        ...report,
        id: createQueueId(),
        createdAt: Date.now(),
        attempts: 0,
    } as OfflineQueuedReport;
    try {
        await migrateLegacyQueue();
        await db.offlineReportQueue.put(queuedReport);
    } catch (error) {
        throw new OfflineReportQueueError(error instanceof Error ? error.message : undefined);
    }
    emitQueueChanged();
    return queuedReport;
}

export async function removeQueuedReport(id: string): Promise<void> {
    await db.offlineReportQueue.delete(id);
    emitQueueChanged();
}

export async function clearQueuedReports(ownerUserId?: string): Promise<void> {
    if (ownerUserId) {
        await db.offlineReportQueue.where("ownerUserId").equals(ownerUserId).delete();
    } else {
        await db.offlineReportQueue.clear();
        if (typeof window !== "undefined" && typeof window.localStorage !== "undefined") {
            window.localStorage.removeItem(LEGACY_STORAGE_KEY);
        }
    }
    emitQueueChanged();
}

export async function markQueuedReportAttempt(
    id: string,
    error: unknown,
    nextAttemptAt?: number,
): Promise<void> {
    const message = error instanceof Error ? error.message : "Upload failed";
    await db.offlineReportQueue
        .where("id")
        .equals(id)
        .modify((report) => {
            report.attempts = ((report.attempts as number) ?? 0) + 1;
            report.lastAttemptAt = Date.now();
            report.lastError = message;
            if (nextAttemptAt !== undefined) {
                report.nextAttemptAt = nextAttemptAt;
            }
        });
    emitQueueChanged();
}

function errorText(error: unknown): string {
    if (error instanceof Error) return error.message;
    if (typeof error === "string") return error;
    if (isObject(error) && typeof error.message === "string") return error.message;
    return "";
}

const RATE_LIMIT_BUFFER_MS = 2000;

function boundedRetryMs(retryAfterMs: number): number {
    const bounded = Math.min(Math.max(retryAfterMs, 1000), 60 * 60 * 1000);
    return bounded + RATE_LIMIT_BUFFER_MS;
}

function rateLimitDelayFromData(data: unknown): number | null {
    if (!isObject(data) || data.code !== "RATE_LIMITED") return null;
    if (typeof data.retryAfterMs !== "number" || !Number.isFinite(data.retryAfterMs)) return null;
    return boundedRetryMs(data.retryAfterMs);
}

/** Milliseconds to wait before retrying, or null when the error is not a rate limit. */
export function rateLimitRetryDelayMs(error: unknown): number | null {
    if (error instanceof ConvexError) {
        const fromData = rateLimitDelayFromData(error.data);
        if (fromData !== null) return fromData;
    }

    const message = errorText(error);
    if (!/rate limit exceeded/i.test(message)) return null;

    const match = /try again in\s+(\d+)\s+seconds/i.exec(message);
    const seconds = match ? Number(match[1]) : 5 * 60;
    if (!Number.isFinite(seconds)) return 5 * 60 * 1000;
    return boundedRetryMs(Math.min(Math.max(seconds, 1), 60 * 60) * 1000);
}

export function isRateLimitError(error: unknown): boolean {
    return rateLimitRetryDelayMs(error) !== null;
}

export function describeRateLimitWait(delayMs: number): string {
    const minutes = Math.max(1, Math.ceil(delayMs / 60_000));
    const when = minutes === 1 ? "about a minute" : `about ${minutes} minutes`;
    return `Saved on this device. It will upload automatically in ${when}.`;
}

export async function fileToOfflineStoredImage(
    file: File,
    profile: ImageUploadProfile = "standard",
): Promise<OfflineStoredImage> {
    const uploadFile = await convertImageFileToWebPInternal(file, profile);
    const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new OfflineReportQueueError("Could not save the image for offline upload"));
        reader.onload = () => {
            if (typeof reader.result === "string") {
                resolve(reader.result);
            } else {
                reject(new OfflineReportQueueError("Could not save the image for offline upload"));
            }
        };
        reader.readAsDataURL(uploadFile);
    });

    return {
        name: uploadFile.name,
        type: uploadFile.type,
        lastModified: uploadFile.lastModified,
        dataUrl,
    };
}

function parseImageDataUrl(dataUrl: string): { mimeType: string; base64Data: string } | null {
    const match = /^data:(image\/[a-z0-9.+-]+);base64,([a-z0-9+/=\s]+)$/i.exec(dataUrl);
    if (!match) return null;
    return {
        mimeType: match[1].toLowerCase(),
        base64Data: match[2].replace(/\s/g, ""),
    };
}

export function offlineStoredImageToBlob(image: OfflineStoredImage): Blob {
    const parsed = parseImageDataUrl(image.dataUrl);
    if (!parsed) {
        throw new OfflineReportQueueError("Could not read the saved image for offline upload.");
    }

    const binary = atob(parsed.base64Data);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
    }
    return new Blob([bytes], { type: image.type || parsed.mimeType });
}

export function getOfflineReportOwnerId(user: unknown): string | undefined {
    if (!isObject(user)) return undefined;
    return typeof user.id === "string" ? user.id : undefined;
}

export function isOfflineOrNetworkError(error: unknown): boolean {
    if (error instanceof OfflineReportQueueError) return false;
    if (typeof navigator !== "undefined" && navigator.onLine === false) return true;

    const message =
        error instanceof Error
            ? `${error.name} ${error.message}`.toLowerCase()
            : typeof error === "string"
              ? error.toLowerCase()
              : "";

    return (
        message.includes("network") ||
        message.includes("fetch failed") ||
        message.includes("failed to fetch") ||
        message.includes("connection") ||
        message.includes("timeout") ||
        message.includes("timed out")
    );
}

